import axios from "axios";
import classNames from "classnames";
import { formRegEx, formRegExReplacer } from "constant/regex";
import moment from "moment";
import { toast } from "react-toastify";
import {
  CURRENCY,
  notProtectedRoute,
  permissionRoute,
} from "../constant/constants";
import { routes } from "@/routes";
import { imageUrl } from "@/config/apiUrl";

export const apiHeader = (token, isFormData) => {
  const headers = {
    "ngrok-skip-browser-warning": "69420",
    "timezone-offset": moment().utcOffset(),
    "Content-Type": isFormData ? "multipart/form-data" : "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return { headers };
};

export const formatMessageDate = (date) => {
  const messageDate = moment(date);
  const now = moment();

  if (messageDate.isSame(now, "minute")) {
    // If the date is exactly now, show the current time, e.g., 'HH:mm A'
    return now.format("HH:mm A");
  } else if (messageDate.isSame(now, "day")) {
    // If the date is today, use fromNow()
    return messageDate.fromNow();
  } else if (messageDate.isSame(now.subtract(1, "day"), "day")) {
    // If the date is yesterday, use fromNow()
    return messageDate.fromNow();
  } else {
    // Otherwise, show the date in a standard format, e.g., 'DD/MM/YYYY'
    return messageDate.format("DD/MM/YYYY");
  }
};

export const formatDate = (date, format = "MM/DD/YYYY") => {
  const formattedDate = moment(date);
  return formattedDate.format(format);
};

export const formatTime = (time, format = "hh:mm A") => {
  const formattedTime = moment(time);
  return formattedTime.format(format);
};
export const formatDateTime = (date, format = "MM/DD/YYYY HH:mm a") => {
  const formattedDate = moment(date);
  return formattedDate.format(format);
};
export function stripAllHtmlTags(input) {
  return input?.replace(/<[^>]*>?/gm, "");
}

export const scrollToTop = (topVal = 0) => {
  window.scrollTo({
    top: topVal,
    behavior: "smooth",
  });
};

export const formatNumber = (num, showCurrency = true) => {
  if (num % 1 === 0) {
    return `${showCurrency ? CURRENCY : ""}${num?.toLocaleString()}`;
  } else {
    return `${showCurrency ? CURRENCY : ""}${num
      ?.toFixed(2)
      ?.toLocaleString()}`;
  }
};

export const quillValidateHandler = (
  params,
  toastMessage = "Please fill the home cms field!",
) => {
  let flag = true;
  for (let key in params) {
    const parsedHTML = new DOMParser().parseFromString(
      params[key],
      "text/html",
    );
    const plainText = parsedHTML.body.textContent || "";
    if (plainText?.trim() === "") {
      toast.error(toastMessage);
      flag = false;
      break;
    }
  }
  return flag;
};

export const cn = (...args) => classNames(...args);

export const validateDate = (date) => {
  if (!date) return null; // Return null if date is falsy

  // Check if date is a moment object and has a $d property
  if (date?.$d) {
    return moment(date.$d).format(); // Format if $d exists
  } else {
    return moment(date).format(); // Assume date is a valid input for moment
  }
};

export const validateUrl = (url) => {
  const urlRegex =
    /^(https?:\/\/)([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
  return urlRegex.test(url);
};

export const camelCaseToLower = (str) => {
  return str.replace(formRegEx, formRegExReplacer).toLowerCase();
};

export const urlPattern =
  /^(https?:\/\/)(www\.)?([\w-]+\.)+[a-z]{2,6}(\/[\w-]*)*\/?$/;

export const getYearRange = () => {
  const years = [
    ...Array.from({ length: 5 }).map((_, i) => ({
      label: `${moment().subtract(i, "year").format("YYYY")}`,
      value: `${moment().subtract(i, "year").format("YYYY")}`,
    })),
  ];
  return years;
};

export const RenderToast = ({ type = "error", message = "" }) => {
  let toastId = "error-render-toast";

  if (!type || !message) return;

  toast[type](message, {
    id: toastId,
  });
};

export const validateNestedParams = (
  params,
  parentKey = "",
  options = { disallowEmptyArray: true },
) => {
  const formatKey = (rawKey) => {
    return rawKey
      .replace(/\[(\d+)\]/g, (_, i) => ` ${parseInt(i) + 1}`) // [0] => 1
      .split(".")
      .map(
        (part) =>
          part
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase()), // capitalize
      )
      .join(" → ");
  };

  if (Array.isArray(params)) {
    if (options.disallowEmptyArray && params.length === 0) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }

    for (let i = 0; i < params.length; i++) {
      const key = `${parentKey}[${i}]`;
      if (!validateNestedParams(params[i], key, options)) return false;
    }
  } else if (typeof params === "object" && params !== null) {
    const keys = Object.keys(params);
    if (keys.length === 0 && parentKey) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }

    for (const key of keys) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      if (!validateNestedParams(params[key], fullKey, options)) return false;
    }
  } else {
    if (params === "" || params === null || params === undefined) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }
  }

  return true;
};

export const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const CreateFormData = (data) => {
  const formData = new FormData();
  for (let key in data) {
    formData.append(key, data[key]);
  }
  return formData;
};

export function checkPermission(permissions, main, sub) {
  const permission = permissions?.find((item) => item?.main === main);
  if (permission) {
    return permission?.sub?.includes(sub);
  }
  return false;
}

export const getFirstRoute = (url) => {
  // Check if the URL is just "/"
  if (url === "/") {
    return "/";
  }

  // Split the URL by '/' and filter out empty values
  const routes = url.split("/").filter((route) => route);

  // Return the first route as '/route'
  return `/${routes?.[0]}`;
};

// filter the routes based on the user's permissions
export const filterRoutes = (user) => {
  const permissions = user?.role?.permissions;
  const filteredRoutes = [];
  const filteredPermissions = permissions?.map((item) => item?.main);

  for (const item of routes) {
    if (item.path === "/system-config" && user?.role?.name !== "admin")
      continue;
    const firstRoute = getFirstRoute(item.path);
    if (notProtectedRoute.includes(firstRoute)) {
      filteredRoutes.push(item);
    } else {
      const permission = filteredPermissions?.includes(
        permissionRoute[firstRoute],
      );
      if (permission) {
        filteredRoutes.push(item);
      }
    }
  }

  return filteredRoutes;
};

export const getID = (id) => (id ? id.slice(id.length - 7, id.length) : "");

export const getInitials = (text = "") => {
  const words = text.trim().split(/\s+/);

  if (words.length >= 2) {
    return words[0].charAt(0).toUpperCase() + words[1].charAt(0).toUpperCase();
  } else {
    return words[0].slice(0, 2).toUpperCase();
  }
};

export const getUserName = (user) => {
  return `${user?.firstName} ${user?.lastName}`;
};
export const capitalize = (word) => {
  if (!word) return word;
  return word.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};
export const filterPermissions = (permissions) => {
  const filteredPermissions = [
    "role-management",
    "staff-management",
    "state-management",
  ];
  return permissions?.filter(
    (permission) => !filteredPermissions.includes(permission?.main),
  );
};
const isDuplicate = (array) => {
  return array.reduce((acc, curr) => {
    if (acc[curr]) {
      acc[curr]++;
    } else {
      acc[curr] = 1;
    }
    return acc;
  }, {});
};
export const validateArrayOfStrings = (array, fieldLabel = "Field") => {
  for (let i = 0; i < array.length; i++) {
    const item = array[i]?.trim();
    if (!item) {
      RenderToast({
        type: "error",
        message: `${fieldLabel} ${i + 1} is required`,
      });
      return false;
    }
  }
  const duplicateEmails = isDuplicate(array);
  for (const [key, value] of Object.entries(duplicateEmails)) {
    if (value > 1) {
      RenderToast({
        type: "error",
        message: `${fieldLabel} value "${key}" is duplicate`,
      });
      return false;
    }
  }

  return true;
};
export const mergeArray = (array1, array2) => {
  return [...array1, ...array2]?.filter(Boolean);
};
export const productImages = (product) => {
  return imageUrl(
    mergeArray(product?.itemImages || [], product?.images || [])?.[0],
  );
};
export const truncate = (text, maxLength = 50, suffix = "...") => {
  if (!text || typeof text !== "string") return text || "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + suffix;
};
